import { DefaultRootState, PortalAuthState } from 'react-redux';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  PortalUser,
  Company as ApiCompany,
  PortalLoginStatusResponse,
  PortalAuthenticationResponse,
} from '@idk-web/api';

const initialState: PortalAuthState = {
  status: 'LOGGED_OUT',
  id: null,
  token: null,
  user: null,
  currentCompany: null,
  availableCompanies: [],
  sso: undefined,
};

export const portalAuthSlice = createSlice({
  name: 'portal/auth',
  initialState: initialState as PortalAuthState,
  reducers: {
    logout(): PortalAuthState {
      return initialState;
    },
    login(
      _state,
      action: PayloadAction<
        Extract<PortalLoginStatusResponse, { status: 'COMPLETED' }>
      >,
    ): PortalAuthState {
      const payload = action.payload;

      return {
        status: 'LOGGED_IN',
        id: payload.id,
        token: null,
        user: {
          id: payload.user.id,
          username: payload.user.username,
          displayName: payload.user.displayName ?? '',
          email: payload.user.email,
          country: payload.user.country,
          mustChangePassword: payload.mustChangePassword,
          hasValidatedEmail: payload.hasValidatedEmail,
        },
        currentCompany: null,
        availableCompanies: payload.companies.map((company) => ({
          id: company.companyId,
          code: company.companyCode,
          name: company.companyName,
          isAdmin: company.isAdmin,
        })),
        sso: payload.sso,
      };
    },
    updateUser(state, action: PayloadAction<PortalUser>): void {
      if (state.user) {
        state.user.email = action.payload.email;
        state.user.mustChangePassword = action.payload.mustChangePassword;
        state.user.hasValidatedEmail = action.payload.hasValidatedEmail;
      }
    },
    authenticate(
      state,
      action: PayloadAction<{
        auth: PortalAuthenticationResponse;
        config: ApiCompany;
      }>,
    ): PortalAuthState {
      const { auth, config } = action.payload;

      return {
        status: 'AUTHENTICATED',
        id: state.id ?? '',
        token: auth.token,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        user: state.user!,
        currentCompany: {
          id: config.id,
          code: config.code,
          name: auth.company.name,
          isAdmin: auth.user.isAdmin,
          services: auth.user.availableServices,
          countries: config.configurations.map((config) => ({
            teleidEnabled: config.teleidEnabled,
            teleidSsnEnabled: config.teleidSsnEnabled,
            teleidSsnNorwayEnabled: config.teleidSsnNorwayEnabled,
            teleidEmailEnabled: config.teleidEmailEnabled,
            teleidSmsEnabled: config.teleidSmsEnabled,
            teleidChatEnabled: config.teleidChatEnabled,
            teleidMaxUsersNumber: config.teleidMaxUsersNumber,
            teleidCounter: config.teleidCounter,
            telesignEnabled: config.telesignEnabled,
            telesignSsnEnabled: config.telesignSsnEnabled,
            telesignEmailEnabled: config.telesignEmailEnabled,
            telesignSmsEnabled: config.telesignSmsEnabled,
            telesignChatEnabled: config.telesignChatEnabled,
            telesignCounter: config.telesignCounter,
            telesignMaxUsersNumber: config.telesignMaxUsersNumber,
            safemailEnabled: config.safemailEnabled,
            safemailEmailEnabled: config.safemailEmailEnabled,
            safemailSmsEnabled: config.safemailSmsEnabled,
            safemailCounter: config.safemailCounter,
            safemailMaxUsersNumber: config.safemailMaxUsersNumber,
            provider: config.provider,
            country: config.country,
            defaultLanguage: config.defaultLanguage,
          })),
          defaultStartTab: auth.company.defaultStartTab,
          defaultBankIdCountry: auth.company.defaultCountry,
          logAccess: auth.user.canSeeLogs,
          telesignDefaultText: auth.company.telesignDefaultText,
          telesignDefaultTextDisabled: auth.company.telesignDefaultTextDisabled,
          formIdOnlySafemail: auth.company.formIdOnlySafemail,
          forms: config.forms.map((form) => ({
            id: form.id,
            name: form.name,
            path: form.path,
            email: form.email,
            autodeleteCountDays: form.autodeleteCountDays ?? 180,
            providers: form.providers,
          })),
          requestSignatureEnabled: auth.company.requestSignatureEnabled,
          safemailRepliesEnabled: config.safemailRepliesEnabled,
          safemailSignRequiresAttachment:
            auth.company.safemailSignRequiresAttachment,
          safemailMultipleAttachments: auth.company.safemailMultipleAttachments,
          autodeleteCountDays: config.autodeleteCountDays,
          companyLogoUrl: auth.company.logoUrl,
          apiEnabled: config.apiEnabled,
          apiClientIp: config.apiClientIp?.split(',') ?? [],
          sentAccess: config.sentAccess,
          timezone: config.timezone,
          usernamePasswordNewAccountsDisabled:
            config.usernamePasswordNewAccountsDisabled,
          companySignatoryEnabled: config.companysignatory,
          sparEnabled: config.spar,
          apiKey: config.apiKey,
          apiToken: config.apiToken,
        },
        availableCompanies: state.availableCompanies,
        sso: state.sso,
      };
    },
  },
});

export const { logout, login, authenticate, updateUser } =
  portalAuthSlice.actions;

export const selectPortalLoginStatus = (state: DefaultRootState) =>
  state.portalAuth.status;
export const selectPortalUser = (state: DefaultRootState) =>
  state.portalAuth.user;
export const selectPortalCompany = (state: DefaultRootState) =>
  state.portalAuth.currentCompany;
export const selectAvailablePortalCompanies = createSelector(
  (state: DefaultRootState) => state.portalAuth.availableCompanies,
  (state: DefaultRootState) => state.portalAuth.currentCompany,
  (availableCompanies, currentCompany) => {
    return availableCompanies.filter(
      (company) => company.id !== currentCompany?.id,
    );
  },
);
export const selectPortalSso = (state: DefaultRootState) =>
  state.portalAuth.sso;

export default portalAuthSlice.reducer;
